import React, { useState, useEffect } from "react";
import styles from "./CreateAnswerModeModal.module.css";
import { AnswerMode, PromptExample } from "../AnswerModeContainer/AnswerMode";
import { v4 as uuidv4 } from "uuid";
import {
  MODEL_CONFIG,
  MODEL_REASONING_MAP,
  ReasoningEffort,
  normalizeReasoningEffortForModel,
} from "../../question-input-container/ModelSettingsPopup/ModelSettingsPopup";
import {
  Dismiss24Regular,
  Person20Regular,
  TargetArrow20Regular,
  TextDescription20Regular,
  Beaker20Regular,
  Add20Regular,
  Delete20Regular,
  ChevronDown16Regular,
  ErrorCircle20Regular,
  ArrowTrendingLines20Regular,
  ShieldCheckmark20Regular,
  DataBarVertical20Regular,
  DocumentSearch24Regular,
  DocumentCheckmark24Regular,
  Info24Regular,
} from "@fluentui/react-icons";

import { FileDropZone } from "../../../components/files/FileDropZone/FileDropZone";
import { FileList } from "../../../components/files/FileList/FileList";
import {
  generatePromptApi,
  PromptGenerationUpdate,
  ConversationPromptResponse,
} from "../../../api";
import { useLogin } from "../../../utils/authConfig";
import { getToken } from "../../../utils/authConfig";
import { useMsal } from "@azure/msal-react";
import { AutoResizeTextarea } from "../../../components/text/AutoResizeTextArea/AutoResizeTextArea";
import { readJsonStream } from "../../../utils/streamUtils";
import { TEMPLATES } from "./templates";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (mode: AnswerMode) => void;
  onDelete?: (id: string) => void;
  nextOrderIndex: number;
  existingModeNames: string[];
  initialMode?: AnswerMode | null;
  externalIsGenerating?: boolean;
  externalGenerationProgress?: PromptGenerationUpdate | null;
  externalDraft?: ConversationPromptResponse | null;
}

const CreateAnswerModeModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  nextOrderIndex,
  existingModeNames,
  initialMode,
  externalIsGenerating,
  externalGenerationProgress,
  externalDraft,
}: Props) => {
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);

  // Auth
  const client = useLogin ? useMsal().instance : undefined;

  // Validation State
  const [nameError, setNameError] = useState<string | null>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] =
    useState<PromptGenerationUpdate | null>(null);

  // Identity State
  const [title, setTitle] = useState("");
  const [role, setRole] = useState("");
  const [task, setTask] = useState("");

  // Behavior State
  const [format, setFormat] = useState("");
  const [examples, setExamples] = useState<PromptExample[]>([]);

  // Model Settings State
  const [selectedModel, setSelectedModel] = useState("gpt-6.1-sol");
  const [reasoningValue, setReasoningValue] = useState<number>(1);

  // --- Document Prompt Synthesis State ---
  const [useDocumentMode, setUseDocumentMode] = useState(false);

  // 1. Text Inputs for the Generator
  const [generatorInputText, setGeneratorInputText] = useState("");
  const [generatorOutputText, setGeneratorOutputText] = useState("");

  // 2. File Arrays for the Generator
  const [inputFiles, setInputFiles] = useState<File[]>([]);
  const [outputFiles, setOutputFiles] = useState<File[]>([]);
  const showFileLists = inputFiles.length > 0 || outputFiles.length > 0;

  // External progress driven by conversation-to-prompt workflow in Chat.tsx
  // Internal progress is used by the manual AI generator inside this modal
  const activeProgress = externalGenerationProgress ?? generationProgress;

  // Helper: Finds the slider index (0-3) given a model and a value string (e.g., "high", "medium")
  const getReasoningSliderIndex = (
    model: string,
    value: string | undefined,
  ): number => {
    const map = MODEL_REASONING_MAP[model] ?? MODEL_REASONING_MAP["gpt-6.1-sol"];
    const normalizedValue = normalizeReasoningEffortForModel(
      model,
      value ?? "medium",
    );
    if (normalizedValue === map[ReasoningEffort.Pro]) return 3;
    if (normalizedValue === map[ReasoningEffort.Thinking]) return 2;
    if (normalizedValue === map[ReasoningEffort.Normal]) return 1;
    return 0;
  };

  useEffect(() => {
    if (isOpen) {
      if (externalDraft) {
        // Raw draft from conversation workflow — convert suggestedReasoning
        // using the same slider index map used for editing existing modes.
        setTitle(externalDraft.title ?? "");
        setRole(externalDraft.roleDefinition ?? "");
        setTask(externalDraft.taskDescription ?? "");
        setFormat(externalDraft.outputFormat ?? "");
        setExamples(externalDraft.examples ?? []);
        setSelectedModel("gpt-6.1-sol");
        setReasoningValue(
          getReasoningSliderIndex(
            "gpt-6.1-sol",
            externalDraft.suggestedReasoning,
          ),
        );
        setActiveTemplate(null);
        setNameError(null);
      } else if (initialMode) {
        setTitle(initialMode.name || "");
        setRole(initialMode.roleDefinition || "");
        setTask(initialMode.taskDescription || "");
        setFormat(initialMode.outputFormat || "");
        setExamples(initialMode.examples || []);
        const modelToUse = initialMode.model || "gpt-6.1-sol";
        setSelectedModel(modelToUse);
        setReasoningValue(
          getReasoningSliderIndex(modelToUse, initialMode.reasoningEffort),
        );
        setActiveTemplate(null);
        setNameError(null);
      } else {
        clearFields();
      }
    }
  }, [isOpen, initialMode, externalDraft]);

  const currentModelConfig =
    MODEL_CONFIG[selectedModel] ||
    MODEL_CONFIG["gpt-6.1-sol"] ||
    MODEL_CONFIG["gpt-5.6-terra"];
  const reasoningLabels = ["Fast", "Normal", "Thinking", "Pro"];

  const clearFields = () => {
    setTitle("");
    setRole("");
    setTask("");
    setFormat("");
    setExamples([]);
    setSelectedModel("gpt-6.1-sol");
    setReasoningValue(1);
    setActiveTemplate(null);
    setNameError(null);
    setGeneratorInputText("");
    setGeneratorOutputText("");
    setInputFiles([]);
    setOutputFiles([]);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (newTitle.length > 25)
      setNameError(`Name too long (${newTitle.length}/25)`);
    const normalizedNew = newTitle.trim().toLowerCase();
    if (normalizedNew.length > 0) {
      if (initialMode && initialMode.name.toLowerCase() === normalizedNew) {
        if (newTitle.length <= 25) setNameError(null);
        return;
      }
      const duplicate = existingModeNames.some(
        (existing) => existing.trim().toLowerCase() === normalizedNew,
      );
      if (duplicate) setNameError(`Name "${newTitle}" is taken`);
      else if (newTitle.length <= 25) setNameError(null);
    } else {
      setNameError(null);
    }
  };

  const applyTemplate = (key: "finance" | "safety" | "powerbi") => {
    if (useDocumentMode) return;
    if (activeTemplate === key) {
      clearFields();
      return;
    }
    const t = TEMPLATES[key];
    setTitle(t.title);
    setRole(t.role);
    setTask(t.task);
    setFormat(t.format);
    setExamples(t.examples);
    setActiveTemplate(key);
  };

  const handleGenerateDraft = async () => {
    if (
      !generatorInputText &&
      !generatorOutputText &&
      inputFiles.length === 0 &&
      outputFiles.length === 0
    ) {
      alert("Please provide at least one text example or upload a file.");
      return;
    }

    setIsGenerating(true);
    setGenerationProgress({
      node: "start",
      message: "Initializing workflow...",
      step: 0,
      total_steps: 5,
    });

    let isSuccess = false;

    try {
      const formData = new FormData();
      formData.append("inputText", generatorInputText);
      formData.append("outputText", generatorOutputText);
      inputFiles.forEach((f) => formData.append("inputFiles", f));
      outputFiles.forEach((f) => formData.append("outputFiles", f));

      const token = client ? await getToken(client) : undefined;
      const response = await generatePromptApi(formData, token);

      if (!response.ok || !response.body) {
        throw new Error(`Failed to start generation: ${response.statusText}`);
      }

      const reader = response.body.getReader();

      await readJsonStream<PromptGenerationUpdate>(reader, (update) => {
        console.log(update);
        setGenerationProgress(update);

        if (update.result) {
          isSuccess = true;
          const result = update.result;

          setTitle(result.title);
          setRole(result.roleDefinition);
          setTask(result.taskDescription);
          setFormat(result.outputFormat);
          setExamples(result.examples);

          setReasoningValue(
            getReasoningSliderIndex(selectedModel, result.suggestedReasoning),
          );

          setTimeout(() => {
            setUseDocumentMode(false);
            setIsGenerating(false);
            setGenerationProgress(null);
          }, 800);
        }
      });

      if (!isSuccess) {
        throw new Error(
          "Stream closed unexpectedly without returning a result.",
        );
      }
    } catch (error) {
      console.error("Generation failed", error);
      alert("An error occurred while generating the prompt. Please try again.");
      setIsGenerating(false);
      setGenerationProgress(null);
    }
  };

  const handleFilesAdded = (newFiles: File[], zone: "source" | "target") => {
    if (zone === "source") {
      setInputFiles((prev) => [...prev, ...newFiles]);
    } else {
      setOutputFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (name: string, zone: "source" | "target") => {
    if (zone === "source") {
      setInputFiles((prev) => prev.filter((f) => f.name !== name));
    } else {
      setOutputFiles((prev) => prev.filter((f) => f.name !== name));
    }
  };

  const handleSave = () => {
    if (!title || !role || nameError || title.length > 25) return;

    const rMode = [
      ReasoningEffort.Fast,
      ReasoningEffort.Normal,
      ReasoningEffort.Thinking,
      ReasoningEffort.Pro,
    ][reasoningValue];

    const finalReasoning = normalizeReasoningEffortForModel(
      selectedModel,
      MODEL_REASONING_MAP[selectedModel]?.[rMode],
    );

    const newMode: AnswerMode = {
      id: initialMode ? initialMode.id : uuidv4(),
      name: title,
      roleDefinition: role,
      taskDescription: task,
      outputFormat: format,
      examples: examples,
      isCustom: true,
      orderIndex: initialMode ? initialMode.orderIndex : nextOrderIndex,
      color: "#00a66d",
      icon: "EditPerson20Regular",
      mode: "custom",
      contextRequirement: "any",
      placeholder: `Ask ${title}...`,
      documentMode: true,
      description: task.length > 50 ? task.substring(0, 50) + "..." : task,
      model: selectedModel,
      reasoningEffort: finalReasoning,
    };
    onSave(newMode);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modalContainer}>
        {/* HEADER */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>
              {initialMode
                ? "Edit AI Assistant"
                : "Create your New AI Assistant"}
            </h2>
            <p className={styles.subtitle}>
              Define how your AI assistant thinks, acts, and responds.
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <Dismiss24Regular />
          </button>
        </div>

        {/* BODY */}
        <div className={styles.body}>
          <div className={styles.scrollContent}>
            {/* AI GENERATOR ENTRY */}
            {!initialMode && (
              <div className={styles.generatorToggleWrapper}>
                <button
                  className={`${styles.fullWidthGeneratorBtn} ${
                    useDocumentMode ? styles.activeGeneratorBtn : ""
                  }`}
                  onClick={() => setUseDocumentMode(!useDocumentMode)}
                >
                  <div className={styles.btnContent}>
                    <Beaker20Regular className={styles.btnIcon} />
                    <div className={styles.btnText}>
                      <span className={styles.btnTitle}>
                        AI System Prompt Generator
                      </span>
                      <span className={styles.btnSubtitle}>
                        Provide examples of raw input and desired output to
                        auto-generate your system prompt.
                      </span>
                    </div>
                  </div>
                  <ChevronDown16Regular
                    className={`${styles.chevron} ${
                      useDocumentMode ? styles.rotated : ""
                    }`}
                  />
                </button>
              </div>
            )}

            {useDocumentMode && !initialMode && (
              <div className={styles.aiGeneratorSection}>
                <div className={styles.explainerContainer}>
                  <Info24Regular className={styles.explainerIcon} />
                  <div className={styles.explainerContent}>
                    <span className={styles.explainerTitle}>
                      Let the AI do its Magic
                    </span>
                    <p className={styles.explainerText}>
                      Provide an example of raw input and the polished response
                      you expect. The AI will analyze the difference and write a
                      matching System Prompt to bridge the gap.
                    </p>
                    <ul className={styles.explainerList}>
                      <li>
                        <strong>Input (Left):</strong> What the user sends
                        (e.g., messy notes, emails). Paste text or upload files.
                      </li>
                      <li>
                        <strong>Output (Right):</strong> The ideal AI response.
                        Paste text or upload a "perfect example" file.
                      </li>
                      <li>
                        <strong>Flexible:</strong> You can mix and match typed
                        text and uploaded files freely.
                      </li>
                    </ul>
                  </div>
                </div>

                <div className={styles.generatorGrid}>
                  {/* --- LEFT COLUMN (SOURCE/INPUT) --- */}
                  <div className={styles.generatorColumn}>
                    <div className={styles.columnTop}>
                      <div className={styles.columnHeader}>
                        <DocumentSearch24Regular
                          className={styles.headerIcon}
                          style={{ color: "#00a66d" }}
                        />
                        <span className={styles.uploadHeaderText}>
                          Input Examples (What users provide)
                        </span>
                      </div>
                      <p className={styles.uploadSubHeaderText}>
                        Add typical inputs your assistant should handle.
                      </p>
                      <textarea
                        className={styles.ioTextarea}
                        rows={3}
                        placeholder="E.g. Meeting notes, raw incident report, unstructured customer request…"
                        value={generatorInputText}
                        onChange={(e) => setGeneratorInputText(e.target.value)}
                      />
                    </div>
                    <div className={styles.columnBottom}>
                      <div className={styles.uploadInfoSection}>
                        <span className={styles.uploadHeaderText}>
                          Upload input files (optional)
                        </span>
                        <p className={styles.uploadSubHeaderText}>
                          Upload documents representing raw input.
                        </p>
                      </div>
                      <FileDropZone
                        onFilesAdded={(files) =>
                          handleFilesAdded(files, "source")
                        }
                      />
                      {showFileLists && (
                        <div style={{ marginTop: "10px" }}>
                          <FileList
                            files={inputFiles}
                            onRemove={(name) =>
                              handleRemoveFile(name, "source")
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* --- RIGHT COLUMN (TARGET/OUTPUT) --- */}
                  <div className={styles.generatorColumn}>
                    <div className={styles.columnTop}>
                      <div className={styles.columnHeader}>
                        <DocumentCheckmark24Regular
                          className={styles.headerIcon}
                          style={{ color: "#00a66d" }}
                        />
                        <span className={styles.uploadHeaderText}>
                          Output Examples (Desired Response)
                        </span>
                      </div>
                      <p className={styles.uploadSubHeaderText}>
                        Show us what a great answer looks like.
                      </p>
                      <textarea
                        className={styles.ioTextarea}
                        rows={3}
                        placeholder="Paste the polished response you'd like the assistant to produce…"
                        value={generatorOutputText}
                        onChange={(e) => setGeneratorOutputText(e.target.value)}
                      />
                    </div>
                    <div className={styles.columnBottom}>
                      <div className={styles.uploadInfoSection}>
                        <span className={styles.uploadHeaderText}>
                          Upload output files (optional)
                        </span>
                        <p className={styles.uploadSubHeaderText}>
                          Upload examples of well‑formatted outputs.
                        </p>
                      </div>
                      <FileDropZone
                        onFilesAdded={(files) =>
                          handleFilesAdded(files, "target")
                        }
                      />
                      {showFileLists && (
                        <div style={{ marginTop: "10px" }}>
                          <FileList
                            files={outputFiles}
                            onRemove={(name) =>
                              handleRemoveFile(name, "target")
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Internal generator progress/button — only for manual generator flow */}
                {isGenerating && generationProgress ? (
                  <div className={styles.progressContainer}>
                    <div className={styles.progressHeader}>
                      <span className={styles.progressTitle}>
                        {generationProgress.node === "critique_draft"
                          ? "QA Review"
                          : "AI Architect"}
                      </span>
                      <span className={styles.progressStep}>
                        Step {generationProgress.step}/
                        {generationProgress.total_steps}
                      </span>
                    </div>
                    <div className={styles.progressBarTrack}>
                      <div
                        className={styles.progressBarFill}
                        style={{
                          width: `${
                            (generationProgress.step /
                              generationProgress.total_steps) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                    <p className={styles.progressMessage}>
                      {generationProgress.message}
                    </p>
                    {generationProgress.meta_data?.feedback && (
                      <p className={styles.feedbackText}>
                        ⚠️ Adjustment:{" "}
                        {generationProgress.meta_data.feedback.substring(0, 60)}
                        ...
                      </p>
                    )}
                  </div>
                ) : (
                  <div className={styles.generatorFooter}>
                    <button
                      className={styles.generateDraftBtn}
                      onClick={handleGenerateDraft}
                    >
                      Generate System Prompt Draft
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* EXTERNAL PROGRESS BAR — shown when triggered from chat conversation,
                regardless of whether the generator accordion is open */}
            {externalIsGenerating && activeProgress && (
              <div className={styles.progressContainer}>
                <div className={styles.progressHeader}>
                  <span className={styles.progressTitle}>
                    {activeProgress.node === "critique_draft"
                      ? "QA Review"
                      : "AI Architect"}
                  </span>
                  <span className={styles.progressStep}>
                    Step {activeProgress.step}/{activeProgress.total_steps}
                  </span>
                </div>
                <div className={styles.progressBarTrack}>
                  <div
                    className={styles.progressBarFill}
                    style={{
                      width: `${
                        (activeProgress.step / activeProgress.total_steps) * 100
                      }%`,
                    }}
                  />
                </div>
                <p className={styles.progressMessage}>
                  {activeProgress.message}
                </p>
                {activeProgress.meta_data?.feedback && (
                  <p className={styles.feedbackText}>
                    ⚠️ Adjustment:{" "}
                    {activeProgress.meta_data.feedback.substring(0, 60)}...
                  </p>
                )}
              </div>
            )}

            {/* [TEMPLATE SWITCHER] */}
            {!initialMode && (
              <div className={styles.templateSection}>
                <div className={styles.templateGrid}>
                  <span className={styles.templateHelperText}>
                    Start with a Template
                  </span>
                  <button
                    className={`${styles.templateChip} ${
                      activeTemplate === "finance" ? styles.activeChip : ""
                    }`}
                    onClick={() => applyTemplate("finance")}
                  >
                    <ArrowTrendingLines20Regular /> Risk & Finance
                  </button>
                  <button
                    className={`${styles.templateChip} ${
                      activeTemplate === "safety" ? styles.activeChip : ""
                    }`}
                    onClick={() => applyTemplate("safety")}
                  >
                    <ShieldCheckmark20Regular /> HSSE Safety
                  </button>
                  <button
                    className={`${styles.templateChip} ${
                      activeTemplate === "powerbi" ? styles.activeChip : ""
                    }`}
                    onClick={() => applyTemplate("powerbi")}
                  >
                    <DataBarVertical20Regular /> PowerBI Helper
                  </button>
                </div>
              </div>
            )}

            {/* [DASHBOARD GRID] */}
            <div className={styles.dashboardGrid}>
              {/* AREA: NAME */}
              <div className={`${styles.inputGroup} ${styles.areaName}`}>
                <label className={styles.label}>
                  Name <span style={{ color: "#00a66d" }}>*</span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontWeight: 400,
                      color: title.length > 25 ? "#d32f2f" : "#ccc",
                      fontSize: "0.75rem",
                    }}
                  >
                    {title.length}/25
                  </span>
                </label>
                <input
                  className={`${styles.inputField} ${
                    nameError ? styles.inputError : ""
                  }`}
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="e.g. Meeting Summarizer"
                  maxLength={25}
                />
                {nameError && (
                  <span className={styles.errorMessage}>
                    <ErrorCircle20Regular
                      style={{ fontSize: 12, marginRight: 4 }}
                    />{" "}
                    {nameError}
                  </span>
                )}
              </div>

              {/* AREA: MODEL */}
              <div className={`${styles.inputGroup} ${styles.areaModel}`}>
                <label className={styles.label}>Model Engine</label>
                <div className={styles.selectWrapper}>
                  <select
                    className={styles.selectInput}
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                  >
                    {Object.keys(MODEL_CONFIG).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <ChevronDown16Regular className={styles.selectArrow} />
                </div>
              </div>

              {/* AREA: ROLE */}
              <div className={`${styles.inputGroup} ${styles.areaRole}`}>
                <label className={styles.label}>
                  <Person20Regular /> Role Definition{" "}
                  <span style={{ color: "#00a66d" }}>*</span>
                </label>
                <AutoResizeTextarea
                  className={`${styles.textarea} ${styles.roleTextarea}`}
                  value={role}
                  onChange={(e: any) => setRole(e.target.value)}
                  placeholder="You are an efficient executive assistant..."
                  minLines={3}
                />
              </div>

              {/* AREA: REASONING (Slider) */}
              {currentModelConfig.allowReasoningEffort && (
                <div
                  className={`${styles.sliderContainer} ${styles.areaReasoning}`}
                >
                  <div className={styles.sliderHeader}>
                    <span className={styles.label}>Reasoning Effort</span>
                    <span className={styles.sliderValue}>
                      {reasoningLabels[reasoningValue]}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="3"
                    step="1"
                    className={styles.rangeInput}
                    value={reasoningValue}
                    onChange={(e) =>
                      setReasoningValue(parseInt(e.target.value))
                    }
                  />
                </div>
              )}

              {/* AREA: TASK */}
              <div className={`${styles.inputGroup} ${styles.areaTask}`}>
                <label className={styles.label}>
                  <TargetArrow20Regular /> Task & Goal
                </label>
                <AutoResizeTextarea
                  className={`${styles.textarea} ${styles.taskTextarea}`}
                  value={task}
                  onChange={(e: any) => setTask(e.target.value)}
                  placeholder="Analyze rough meeting notes..."
                  minLines={3}
                />
              </div>

              {/* AREA: FORMAT */}
              <div className={`${styles.inputGroup} ${styles.areaFormat}`}>
                <label className={styles.label}>
                  <TextDescription20Regular /> Output Format
                </label>
                <AutoResizeTextarea
                  className={`${styles.textarea} ${styles.formatTextarea}`}
                  value={format}
                  onChange={(e: any) => setFormat(e.target.value)}
                  placeholder={`## Summary\n...`}
                  minLines={3}
                />
              </div>
            </div>

            {/* EXAMPLES SECTION */}
            <div className={styles.examplesSection}>
              <div className={styles.sectionTitle}>
                <Beaker20Regular /> Few-Shot Examples (Optional)
              </div>
              {examples.map((ex, i) => (
                <div key={i} className={styles.exampleCard}>
                  <div className={styles.exampleHeader}>
                    <span className={styles.exampleTitle}>Example {i + 1}</span>
                    <button
                      className={styles.closeButton}
                      onClick={() => {
                        const n = [...examples];
                        n.splice(i, 1);
                        setExamples(n);
                      }}
                    >
                      <Delete20Regular />
                    </button>
                  </div>
                  <div className={styles.exampleGrid}>
                    <div className={styles.exampleInputWrapper}>
                      <AutoResizeTextarea
                        className={styles.exampleTextarea}
                        placeholder="Example Input..."
                        value={ex.input}
                        onChange={(e: any) => {
                          const n = [...examples];
                          n[i].input = e.target.value;
                          setExamples(n);
                        }}
                        minLines={1}
                      />
                    </div>
                    <div className={styles.exampleInputWrapper}>
                      <AutoResizeTextarea
                        className={styles.exampleTextarea}
                        placeholder="Example Output..."
                        value={ex.output}
                        onChange={(e: any) => {
                          const n = [...examples];
                          n[i].output = e.target.value;
                          setExamples(n);
                        }}
                        minLines={1}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                className={styles.addExampleBtn}
                onClick={() =>
                  setExamples([...examples, { input: "", output: "" }])
                }
              >
                <Add20Regular /> Add Training Example
              </button>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className={styles.footer}>
          <div>
            {initialMode && onDelete && (
              <button
                className={styles.cancelBtn}
                style={{ color: "#d32f2f" }}
                onClick={() => {
                  if (confirm("Delete this mode?")) {
                    onDelete(initialMode.id);
                    onClose();
                  }
                }}
              >
                Delete AI Assistant
              </button>
            )}
          </div>
          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
            <button
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={!title || !role || !!nameError || title.length > 25}
            >
              {initialMode ? "Save Changes" : "Create AI Assistant"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAnswerModeModal;
